import Keycloak from 'keycloak-js';
import { saveAuthData } from '@/utils/auth';
import { UserRole } from '@/types/models';

const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.roommatik.com',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'roommatik',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'lockers',
});

/**
 * Maps Keycloak resource_access.lockers.roles to the internal UserRole.
 */
function mapKeycloakRole(roles: string[]): UserRole {
  if (roles.includes('admin')) return 'SuperAdmin';
  if (roles.includes('chain-director')) return 'GroupAdmin';
  if (roles.includes('hotel-director')) return 'HotelAdmin';
  if (roles.includes('developer')) return 'Developer';
  if (roles.includes('viewer')) return 'Receptionist';
  return 'Receptionist';
}

/**
 * Persists Keycloak session data into localStorage using the same format
 * as the existing local-auth flow, so the rest of the app works unchanged.
 */
export function storeKeycloakSession(kc: Keycloak): void {
  if (!kc.token || !kc.tokenParsed) return;

  const parsed = kc.tokenParsed;
  const clientId = kc.clientId ?? (import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'lockers');
  const resourceAccess = (parsed as any).resource_access ?? {};
  // Try the active client first, then fall back to 'lockers' (production client).
  // This handles the case where roles are assigned to 'lockers' but the session
  // was issued for a different client alias (e.g. 'dev-lockers').
  const roles: string[] = resourceAccess[clientId]?.roles ?? resourceAccess['lockers']?.roles ?? [];

  const role = mapKeycloakRole(roles);
  // Strip prefix and handle accidental double prefix (e.g. hotel-id-hotel-id-{uuid})
  const rawHotelId = roles.find(r => r.startsWith('hotel-id-'))?.slice('hotel-id-'.length);
  const hotelId = rawHotelId?.startsWith('hotel-id-') ? rawHotelId.slice('hotel-id-'.length) : rawHotelId;
  const rawGroupId = roles.find(r => r.startsWith('chain-id-'))?.slice('chain-id-'.length);
  const groupId = rawGroupId?.startsWith('chain-id-') ? rawGroupId.slice('chain-id-'.length) : rawGroupId;
  const expiresIn = parsed.exp! - Math.floor(Date.now() / 1000);

  // Clear scope keys that may linger from a previous local-auth session
  // before writing new values, so SuperAdmin doesn't inherit a stale hotelId.
  localStorage.removeItem('hotelId');
  localStorage.removeItem('groupId');

  saveAuthData({
    token: kc.token,
    user: {
      id: parsed.sub!,
      name: (parsed as any).name ?? (parsed as any).preferred_username ?? '',
      email: (parsed as any).email ?? '',
      role,
      hotelId,
      groupId,
    },
    expiresIn,
  });
}

export default keycloak;
